import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import DeletionModal from "../DeletionModal";
import LogTimeModal from "./LogTimeModal";

const formatTime = (timeString) => {
  if (!timeString) return "";

  // ISO string check (contains "T")
  if (timeString.includes("T")) {
    return moment(timeString).format("hh:mm A");
  }

  // Fallback for "H:mm" format (like "1:4" or "05:25")
  const [hour, minute] = timeString.split(":").map(Number);
  if (isNaN(hour) || isNaN(minute)) return "";
  return moment({ hour, minute }).format("hh:mm A");
};

function TimeLogs({ data, task, logAdded, onTotalTimeChange, onLogsChanged }) {
  const dispatch = useDispatch();
  const isFirstFetch = useRef(true);

  const [toggleView, setToggleView] = useState(true);
  const [taskLogs, setTaskLogs] = useState([]);
  const [paginationCount, setPaginationCount] = useState(5);
  const [showModal, setShowModal] = useState(null);
  const [updated, setUpdated] = useState(false);
  const [showEditTimeLogModal, setShowEditTimeLogModal] = useState(false);
  const [showDeleteTimeLogModal, setShowDeleteTimeLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const getTimeLogs = await dispatch.time.getTaskTimeLog({
        project_id: data?.project?.id,
        task_id: task?._id,
      });
      const logs = getTimeLogs?.data?.data || [];
      setTaskLogs(logs);
      setShowModal(null);

      if (onTotalTimeChange) {
        let totalMinutes = 0;
        logs.forEach((log) => {
          const [hours, minutes] = (log?.task_time_spent || "0:0")
            .split(":")
            .map(Number);
          totalMinutes += (hours || 0) * 60 + (minutes || 0);
        });
        onTotalTimeChange(
          `${Math.floor(totalMinutes / 60)}:${totalMinutes % 60}`
        );
      }

      // Skip the mount-triggered fetch - only a real add/edit (updated or
      // logAdded actually changing) should tell the task list to refresh.
      if (!isFirstFetch.current && onLogsChanged) {
        onLogsChanged();
      }
      isFirstFetch.current = false;
    };
    fetchData();
  }, [data?.project?.id, task?._id, updated, logAdded]);

  const toggleLogsView = () => {
    setToggleView(!toggleView);
  };

  const openEditModal = (log) => {
    setShowModal(log);
    setShowEditTimeLogModal(true);
  };

  const openDeleteModal = (e, log) => {
    e.stopPropagation();
    setShowModal(log);
    setShowDeleteTimeLogModal(true);
  };

  const handleLoadMore = () => {
    setPaginationCount(paginationCount + 5);
  };

  return (
    <div className="w-full bg-theme-cream-2 p-4 rounded-md">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 14C5.14348 14 3.36301 13.2625 2.05025 11.9497C0.737498 10.637 0 8.85652 0 7C0 5.14348 0.737498 3.36301 2.05025 2.05025C3.36301 0.737498 5.14348 0 7 0C8.85652 0 10.637 0.737498 11.9497 2.05025C13.2625 3.36301 14 5.14348 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14ZM7 12.6C8.48521 12.6 9.90959 12.01 10.9598 10.9598C12.01 9.90959 12.6 8.48521 12.6 7C12.6 5.51479 12.01 4.09041 10.9598 3.0402C9.90959 1.99 8.48521 1.4 7 1.4C5.51479 1.4 4.09041 1.99 3.0402 3.0402C1.99 4.09041 1.4 5.51479 1.4 7C1.4 8.48521 1.99 9.90959 3.0402 10.9598C4.09041 12.01 5.51479 12.6 7 12.6ZM6.3 7.287V2.8H7.7V6.713L10.465 9.478L9.478 10.465L6.3 7.287Z"
              fill="#606C80"
            />
          </svg>
          <p className="text-sm font-semibold">
            TimeLogs <span className="text-theme-accent">{taskLogs?.length}</span>
          </p>
        </div>
        <div className="flex items-center pb-1">
          <svg
            width="37"
            height="22"
            viewBox="0 0 37 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: toggleView ? "none" : "inline-block" }}
            onClick={toggleLogsView}
          >
            <path d="M22.5 11L14.5 5L14.5 17L22.5 11Z" fill="#656A6F" />
          </svg>
          <svg
            width="45"
            height="18"
            viewBox="0 0 45 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={toggleLogsView}
            style={{ display: toggleView ? "inline-block" : "none" }}
          >
            <path d="M22.5 13L28.5 5L16.5 5L22.5 13Z" fill="#656A6F" />
          </svg>
        </div>
      </div>
      <div
        className={`transition-all duration-500 ease-in-out transform 
    ${
      toggleView
        ? "opacity-100 translate-y-0 max-h-[9999px]"
        : "opacity-0 -translate-y-3 max-h-0 overflow-hidden pointer-events-none"
    }`}
      >
        {taskLogs?.length != 0 ? (
          <div className="pt-8 flex justify-center items-center flex-col gap-2">
            <table className="border-t-2 border-b-2 w-full table-fixed">
              <thead>
                <tr className="text-sm font-medium opacity-85 text-theme-fg-3 border-b-2 border-t-2">
                  <th className="hidden md:table-cell w-16 text-left py-3">Date</th>
                  <th className="w-28 text-left py-3">Who</th>
                  <th className="w-20 text-left py-3">Start</th>
                  <th className="w-20 text-left py-3">End</th>
                  <th className="w-20 text-left py-3">Billable</th>
                  <th className="w-16 text-left py-3">Billed</th>
                  <th className="w-14 text-left py-3">Time</th>
                  <th className="w-10 text-left py-3" />
                </tr>
              </thead>
              {taskLogs?.slice(0, paginationCount).map((task) => {
                return (
                  <tbody key={task._id}>
                    <tr
                      className="text-xs font-medium cursor-pointer hover:bg-theme-cream-2"
                      onClick={() => openEditModal(task)}
                    >
                      <td className="hidden md:table-cell p-2">
                        {moment(task?.date).format("M/D/YY")}
                      </td>
                      <td className="p-2">
                        {task?.user_data?.first_name}{" "}
                        {task?.user_data?.last_name}
                      </td>
                      <td className="p-2">
                        {formatTime(task?.task_start_time)}
                      </td>
                      <td className="p-2">{formatTime(task?.task_end_time)}</td>

                      <td className="p-2 px-5" onClick={(e) => e.stopPropagation()}>
                        {task.isBillable ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_489_3189)">
                              <path
                                d="M8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15ZM8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16Z"
                                fill="#06BD23"
                              />
                              <path
                                d="M10.97 4.97028L10.95 4.99228L7.477 9.41728L5.384 7.32328C5.24183 7.1908 5.05378 7.11868 4.85948 7.12211C4.66518 7.12553 4.47979 7.20425 4.34238 7.34166C4.20497 7.47907 4.12626 7.66446 4.12283 7.85876C4.1194 8.05306 4.19152 8.24111 4.324 8.38328L6.97 11.0303C7.04128 11.1014 7.12617 11.1575 7.21959 11.1951C7.31301 11.2328 7.41305 11.2512 7.51375 11.2493C7.61445 11.2475 7.71374 11.2253 7.8057 11.1843C7.89766 11.1432 7.98041 11.084 8.049 11.0103L12.041 6.02028C12.1769 5.87761 12.2513 5.68718 12.2479 5.49015C12.2445 5.29312 12.1638 5.10533 12.0231 4.96737C11.8823 4.82942 11.693 4.75239 11.4959 4.75293C11.2989 4.75348 11.11 4.83155 10.97 4.97028Z"
                                fill="#06BD23"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_489_3189">
                                <rect width="16" height="16" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_489_3201)">
                              <path
                                d="M8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15ZM8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16Z"
                                fill="#898888"
                              />
                              <path
                                d="M4.64601 4.64592C4.69245 4.59935 4.74763 4.56241 4.80837 4.5372C4.86912 4.512 4.93424 4.49902 5.00001 4.49902C5.06578 4.49902 5.1309 4.512 5.19164 4.5372C5.25239 4.56241 5.30756 4.59935 5.35401 4.64592L8.00001 7.29292L10.646 4.64592C10.6925 4.59943 10.7477 4.56255 10.8084 4.53739C10.8692 4.51223 10.9343 4.49929 11 4.49929C11.0658 4.49929 11.1309 4.51223 11.1916 4.53739C11.2523 4.56255 11.3075 4.59943 11.354 4.64592C11.4005 4.6924 11.4374 4.74759 11.4625 4.80833C11.4877 4.86907 11.5006 4.93417 11.5006 4.99992C11.5006 5.06566 11.4877 5.13076 11.4625 5.1915C11.4374 5.25224 11.4005 5.30743 11.354 5.35392L8.70701 7.99992L11.354 10.6459C11.4005 10.6924 11.4374 10.7476 11.4625 10.8083C11.4877 10.8691 11.5006 10.9342 11.5006 10.9999C11.5006 11.0657 11.4877 11.1308 11.4625 11.1915C11.4374 11.2522 11.4005 11.3074 11.354 11.3539C11.3075 11.4004 11.2523 11.4373 11.1916 11.4624C11.1309 11.4876 11.0658 11.5005 11 11.5005C10.9343 11.5005 10.8692 11.4876 10.8084 11.4624C10.7477 11.4373 10.6925 11.4004 10.646 11.3539L8.00001 8.70692L5.35401 11.3539C5.30752 11.4004 5.25233 11.4373 5.19159 11.4624C5.13085 11.4876 5.06575 11.5005 5.00001 11.5005C4.93426 11.5005 4.86916 11.4876 4.80842 11.4624C4.74769 11.4373 4.6925 11.4004 4.64601 11.3539C4.59952 11.3074 4.56264 11.2522 4.53749 11.1915C4.51233 11.1308 4.49938 11.0657 4.49938 10.9999C4.49938 10.9342 4.51233 10.8691 4.53749 10.8083C4.56264 10.7476 4.59952 10.6924 4.64601 10.6459L7.29301 7.99992L4.64601 5.35392C4.59945 5.30747 4.5625 5.2523 4.5373 5.19155C4.51209 5.13081 4.49911 5.06568 4.49911 4.99992C4.49911 4.93415 4.51209 4.86903 4.5373 4.80828C4.5625 4.74754 4.59945 4.69236 4.64601 4.64592Z"
                                fill="#898888"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_489_3201">
                                <rect width="16" height="16" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        )}
                      </td>
                      <td className="py-2 px-4" onClick={(e) => e.stopPropagation()}>
                        {task.isBilled ? (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_489_3189)">
                              <path
                                d="M8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15ZM8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16Z"
                                fill="#06BD23"
                              />
                              <path
                                d="M10.97 4.97028L10.95 4.99228L7.477 9.41728L5.384 7.32328C5.24183 7.1908 5.05378 7.11868 4.85948 7.12211C4.66518 7.12553 4.47979 7.20425 4.34238 7.34166C4.20497 7.47907 4.12626 7.66446 4.12283 7.85876C4.1194 8.05306 4.19152 8.24111 4.324 8.38328L6.97 11.0303C7.04128 11.1014 7.12617 11.1575 7.21959 11.1951C7.31301 11.2328 7.41305 11.2512 7.51375 11.2493C7.61445 11.2475 7.71374 11.2253 7.8057 11.1843C7.89766 11.1432 7.98041 11.084 8.049 11.0103L12.041 6.02028C12.1769 5.87761 12.2513 5.68718 12.2479 5.49015C12.2445 5.29312 12.1638 5.10533 12.0231 4.96737C11.8823 4.82942 11.693 4.75239 11.4959 4.75293C11.2989 4.75348 11.11 4.83155 10.97 4.97028Z"
                                fill="#06BD23"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_489_3189">
                                <rect width="16" height="16" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        ) : (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_489_3201)">
                              <path
                                d="M8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15ZM8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16Z"
                                fill="#898888"
                              />
                              <path
                                d="M4.64601 4.64592C4.69245 4.59935 4.74763 4.56241 4.80837 4.5372C4.86912 4.512 4.93424 4.49902 5.00001 4.49902C5.06578 4.49902 5.1309 4.512 5.19164 4.5372C5.25239 4.56241 5.30756 4.59935 5.35401 4.64592L8.00001 7.29292L10.646 4.64592C10.6925 4.59943 10.7477 4.56255 10.8084 4.53739C10.8692 4.51223 10.9343 4.49929 11 4.49929C11.0658 4.49929 11.1309 4.51223 11.1916 4.53739C11.2523 4.56255 11.3075 4.59943 11.354 4.64592C11.4005 4.6924 11.4374 4.74759 11.4625 4.80833C11.4877 4.86907 11.5006 4.93417 11.5006 4.99992C11.5006 5.06566 11.4877 5.13076 11.4625 5.1915C11.4374 5.25224 11.4005 5.30743 11.354 5.35392L8.70701 7.99992L11.354 10.6459C11.4005 10.6924 11.4374 10.7476 11.4625 10.8083C11.4877 10.8691 11.5006 10.9342 11.5006 10.9999C11.5006 11.0657 11.4877 11.1308 11.4625 11.1915C11.4374 11.2522 11.4005 11.3074 11.354 11.3539C11.3075 11.4004 11.2523 11.4373 11.1916 11.4624C11.1309 11.4876 11.0658 11.5005 11 11.5005C10.9343 11.5005 10.8692 11.4876 10.8084 11.4624C10.7477 11.4373 10.6925 11.4004 10.646 11.3539L8.00001 8.70692L5.35401 11.3539C5.30752 11.4004 5.25233 11.4373 5.19159 11.4624C5.13085 11.4876 5.06575 11.5005 5.00001 11.5005C4.93426 11.5005 4.86916 11.4876 4.80842 11.4624C4.74769 11.4373 4.6925 11.4004 4.64601 11.3539C4.59952 11.3074 4.56264 11.2522 4.53749 11.1915C4.51233 11.1308 4.49938 11.0657 4.49938 10.9999C4.49938 10.9342 4.51233 10.8691 4.53749 10.8083C4.56264 10.7476 4.59952 10.6924 4.64601 10.6459L7.29301 7.99992L4.64601 5.35392C4.59945 5.30747 4.5625 5.2523 4.5373 5.19155C4.51209 5.13081 4.49911 5.06568 4.49911 4.99992C4.49911 4.93415 4.51209 4.86903 4.5373 4.80828C4.5625 4.74754 4.59945 4.69236 4.64601 4.64592Z"
                                fill="#898888"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_489_3201">
                                <rect width="16" height="16" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        )}
                      </td>
                      <td className="hidden md:table-cell py-2">
                        {task?.task_time_spent}
                      </td>
                      <td className="py-2 text-right pr-2">
                        <button
                          className="p-1.5 rounded hover:bg-theme-full-bg group"
                          onClick={(e) => openDeleteModal(e, task)}
                          type="button"
                          title="Delete time log"
                        >
                          <svg
                            width="14"
                            height="18"
                            viewBox="0 0 14 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="group-hover:opacity-100 opacity-60"
                          >
                            <path
                              d="M10.9033 16.8037H2.8962C2.19382 16.8037 1.70215 16.2418 1.70215 15.6097V5.07394H12.0974V15.5394C12.0974 16.2418 11.5355 16.8037 10.9033 16.8037Z"
                              stroke="#DC3545"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12.8 4.65238V3.38809C12.8 2.89643 12.3786 2.475 11.8869 2.475H9.2881L9.00714 1.42143C8.86667 1.14048 8.65595 1 8.30476 1H5.49524C5.21429 1 4.93333 1.14048 4.79286 1.42143L4.51191 2.40476H1.9131C1.42143 2.40476 1 2.82619 1 3.31786V4.65238C1 4.86309 1.21071 5.07381 1.42143 5.07381H12.3786C12.6595 5.07381 12.8 4.86309 12.8 4.65238Z"
                              stroke="#DC3545"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M4.65234 7.6021V14.2747"
                              stroke="#DC3545"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6.8999 7.60216V14.2748"
                              stroke="#DC3545"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.14771 7.60216V14.2748"
                              stroke="#DC3545"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                );
              })}
            </table>
          </div>
        ) : (
          <p className="text-sm font-medium opacity-55 text-center">
            No Logs to show
          </p>
        )}
        {paginationCount < taskLogs?.length && (
          <p
            onClick={() => handleLoadMore()}
            className="pl-11 mt-3 font-bold text-xs cursor-pointer flex items-center gap-1"
          >
            <span className="text-theme-accent">Load more</span>
            <span>{taskLogs?.length} tasks</span>
          </p>
        )}
      </div>
      {showEditTimeLogModal && (
        <div className="">
          <LogTimeModal
            updated={updated}
            setUpdated={setUpdated}
            data={data}
            setShowEditTimeLogModal={setShowEditTimeLogModal}
            showEditTimeLogModal={showEditTimeLogModal}
            task={showModal}
            parentTaskId={task?._id}
          />
        </div>
      )}

      {showDeleteTimeLogModal && (
        <DeletionModal
          updated={updated}
          setUpdated={setUpdated}
          deleteTaskTimelog={showModal}
          setShowDeletionModal={setShowDeleteTimeLogModal}
        />
      )}
    </div>
  );
}

export default TimeLogs;
